import React, { useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import NavMenuTooltip from "./NavMenuTooltip";
import TrendingUpIcon from "../../assets/img/homepage-banner/banner-trending-up.svg";
import ChevronRightWhiteIcon from "../../assets/img/action-center/chevron-right-white.svg";
import { getBannerData } from "../../utils/recommendations";
import {
  getDismissedPopoverPropertyIds,
  markPopoverDismissedForProperty,
} from "./actionCenterPopoverUtils";
import {
  POPOVER_CAPTION,
  POPOVER_REVENUE_TITLE,
  POPOVER_UNLOCK_TITLE,
  POPOVER_DESCRIPTION,
  POPOVER_CTA_LABEL,
  POPOVER_HEAP_ID_PREFIX,
  POPOVER_TARGET_SELECTOR,
  HOME_ROUTES,
} from "./actionCenterPopoverConstants";
import "./actionCenterPopover.scss";

/**
 * Popover anchored to the Home nav item, shown on every page except the
 * homepage. Points hoteliers at the Action Center: shows the total revenue
 * potential, or the unlock-buyer message when no revenue exists. Hidden
 * entirely when the main action cards aren't available.
 *
 * Shows for either feed: a property whose only cards are v2 (LLM) still gets the
 * popover, though the revenue headline is computed from the v1 promo cards alone.
 *
 * Frequency: once per property per session — dismissing it (X or the CTA)
 * only silences the currently selected property, so switching hotels shows
 * it again for properties the user hasn't acted on. Visiting the homepage
 * merely hides it — it reopens on other pages until dismissed.
 */
const ActionCenterPopover = ({
  data = null,
  v2Data = null,
  propertyId = null,
  isSidebarOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [dismissedIds, setDismissedIds] = useState(
    getDismissedPopoverPropertyIds
  );

  const { show, revenue, hasCorridor } = getBannerData(data, v2Data);
  const isHomePage = HOME_ROUTES.includes(location.pathname);
  const dismissed = propertyId == null || dismissedIds.includes(propertyId);

  if (isHomePage || !show || dismissed) return null;

  const endAppearance = () => {
    markPopoverDismissedForProperty(propertyId);
    setDismissedIds(getDismissedPopoverPropertyIds());
  };

  const handleDismiss = () => {
    endAppearance();
  };

  const handleReviewActions = () => {
    endAppearance();
    // Land on the homepage and auto-scroll to the Action Center section.
    navigate("/", { state: { scrollToActionCenter: true } });
  };

  // Headline mirrors what the payload actually contains: promo revenue wins,
  // the unlock message needs a corridor card, anything else (e.g. ARI-only)
  // gets no headline.
  const headline = revenue
    ? `${POPOVER_REVENUE_TITLE} ${revenue}!`
    : hasCorridor
      ? POPOVER_UNLOCK_TITLE
      : "";

  const title = (
    <span className="action-center-popover__header">
      <img
        src={TrendingUpIcon}
        alt=""
        className="action-center-popover__icon"
      />
      <span className="action-center-popover__header-text">
        <span className="action-center-popover__caption">
          {POPOVER_CAPTION}
        </span>
        {headline && (
          <span className="action-center-popover__headline">{headline}</span>
        )}
      </span>
    </span>
  );

  return (
    <NavMenuTooltip
      isOpen={isSidebarOpen}
      onClose={handleDismiss}
      targetSelector={POPOVER_TARGET_SELECTOR}
      title={title}
      description={POPOVER_DESCRIPTION}
      actionText={POPOVER_CTA_LABEL}
      actionHandler={handleReviewActions}
      actionIcon={ChevronRightWhiteIcon}
      className="action-center-popover"
      heapIdPrefix={POPOVER_HEAP_ID_PREFIX}
    />
  );
};

ActionCenterPopover.propTypes = {
  data: PropTypes.shape({
    propertyId: PropTypes.number,
    results: PropTypes.shape({
      CORRIDOR: PropTypes.array,
      CORRIDOR_REQUEST: PropTypes.array,
      ARI: PropTypes.array,
      PROMO: PropTypes.array,
      BNBERRY: PropTypes.array,
    }),
  }),
  // Action Centre v2 feed. Contributes to whether the popover shows; the revenue
  // headline stays v1-only (see getBannerData).
  v2Data: PropTypes.shape({
    propertyId: PropTypes.number,
    cards: PropTypes.array,
  }),
  propertyId: PropTypes.number,
  isSidebarOpen: PropTypes.bool.isRequired,
};

export default ActionCenterPopover;
